// Grouping and export shapes for the Forms tab. Pure, so they can be tested without React

import type { RawContact } from "../components/types"
import type { FormFieldMeta } from "../components/tabs/VariablesTab"
import type { ExportColumn } from "../components/shared/ExportDialog"
import { formatVarValue } from "../hooks/useVariablesAnalytics"

export interface FormField {
  key: string // "form.<field>"
  meta: FormFieldMeta
}

export interface FormGroup {
  formId: string
  title: string
  fields: FormField[]
}

// Top-level fields of each form, in form order. Group subfields ("form.<group>.<sub>") are left out:
// their values live inside the group field's array, not as their own variable
export function buildFormGroups(formFieldMeta: Record<string, FormFieldMeta>): FormGroup[] {
  const groups = new Map<string, FormGroup>()
  for (const [key, meta] of Object.entries(formFieldMeta)) {
    if (!meta.formId || key.split(".").length !== 2) continue
    if (!groups.has(meta.formId)) {
      groups.set(meta.formId, { formId: meta.formId, title: meta.formTitle || "Untitled form", fields: [] })
    }
    groups.get(meta.formId)!.fields.push({ key, meta })
  }
  return [...groups.values()]
}

const hasValue = (v: unknown) => v !== undefined && v !== null && v !== ""

// Contacts that answered at least one field of the form
export function formRespondents(group: FormGroup, contacts: RawContact[]): RawContact[] {
  return contacts.filter(c => group.fields.some(f => hasValue(c.vars?.[f.key])))
}

function label(meta: FormFieldMeta, v: unknown): string {
  const s = formatVarValue(v)
  return meta.optionLabels?.[s] ?? s
}

// One cell of the responses export. Lists join with "; ", nested values keep their full JSON
export function exportCell(meta: FormFieldMeta, v: unknown): string {
  if (!hasValue(v)) return ""
  if (Array.isArray(v)) {
    return v.every(x => x === null || typeof x !== "object")
      ? v.map(x => label(meta, x)).join("; ")
      : JSON.stringify(v)
  }
  if (typeof v === "object") return JSON.stringify(v)
  return label(meta, v)
}

// One row per respondent, one column per field
export function responsesExport(
  group: FormGroup,
  respondents: RawContact[],
  updatedAt: Record<string, string>,
): { columns: ExportColumn[]; data: Record<string, any>[] } {
  const columns: ExportColumn[] = [
    { key: "contact_name", label: "Contact" },
    { key: "phone", label: "Phone" },
    { key: "updated_at", label: "Response updated" },
    ...group.fields.map((f, i) => ({ key: `f${i}`, label: f.meta.title || f.key.slice("form.".length) })),
  ]
  const data = respondents.map(c => {
    const row: Record<string, any> = {
      contact_name: c.name ?? "",
      phone: c.phone_number ?? "",
      updated_at: updatedAt[`${c.id}:${group.formId}`] ?? "",
    }
    group.fields.forEach((f, i) => { row[`f${i}`] = exportCell(f.meta, c.vars?.[f.key]) })
    return row
  })
  return { columns, data }
}

// One row per field and answer, with counts over the form's respondents. List answers count once per item
export function summaryExport(
  group: FormGroup,
  respondents: RawContact[],
): { columns: ExportColumn[]; data: Record<string, any>[] } {
  const columns: ExportColumn[] = [
    { key: "question", label: "Question" },
    { key: "answer", label: "Answer" },
    { key: "count", label: "Count" },
    { key: "percent", label: "% of respondents" },
  ]
  const total = respondents.length
  const pct = (n: number) => (total ? Math.round((n / total) * 1000) / 10 : 0)
  const data: Record<string, any>[] = []

  for (const f of group.fields) {
    const question = f.meta.title || f.key.slice("form.".length)
    const counts = new Map<string, number>()
    let unanswered = 0
    for (const c of respondents) {
      const v = c.vars?.[f.key]
      if (!hasValue(v)) { unanswered++; continue }
      const items = Array.isArray(v) ? v : [v]
      for (const item of items) {
        const a = item !== null && typeof item === "object" ? formatVarValue(item) : label(f.meta, item)
        counts.set(a, (counts.get(a) ?? 0) + 1)
      }
    }
    for (const [answer, count] of [...counts].sort((a, b) => b[1] - a[1])) {
      data.push({ question, answer, count, percent: pct(count) })
    }
    if (unanswered) data.push({ question, answer: "(no answer)", count: unanswered, percent: pct(unanswered) })
  }
  return { columns, data }
}
